import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import moment from "moment";

import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as userActions } from "store/users";
import { change } from "store/root/actions";
import { CodeDrawer as CodeDrawerForm, standardCodeFormName, uDefCodeFormName } from "utils/codeLib";
import { useStateSelector } from "utils/field";
import convertUnit from "utils/convertUnit";
import getWarningText from "utils/getWarningText";
import { getStandardRVal, getUserDefinedRVal } from "utils/enclosure/api";

import Table from "components/Table";
import Button from "components/Button";
import Drawer from "components/Drawer";
import InputRow from "components/Input/Row";
import Select from "components/Input/Select";
import Tooltip from "components/Tooltip";
import CodeActions from "./CodeActions";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

import AddIcon from "assets/images/icons/JSX/Add";
import EditIcon from "assets/images/icons/JSX/Edit";
import Exclamation from "assets/images/icons/JSX/Exlamation";

const { clearInitCode, setInitCode } = enclosureActions;
const { addToCodeLib } = userActions;

export const headCells = (type) => {
    if (type === "Window") {
        return [
            { id: "name", label: "Code Name", width: "280", align: "left", sortable: true },
            { id: "type", label: "Code Type", width: "136", sortable: true },
            { id: "rVal", label: "Eff. R-Value", width: "126", sortable: true },
            { id: "shgc", label: "SHGC", width: "126", sortable: true },
            { id: "lastEdited", label: "Last Modified", width: "132", sortable: true },
            { id: "actions", label: "", sortable: true },
        ];
    }

    return [
        { id: "name", label: "Code Name", width: "280", align: "left", sortable: true },
        { id: "type", label: "Code Type", width: "136", sortable: true },
        { id: "rVal", label: "Eff. R-Value", width: "126", sortable: true },
        { id: "nominalRVal", label: "Nom. R-Value", width: "126", sortable: true },
        { id: "lastEdited", label: "Last Modified", width: "132", sortable: true },
        { id: "actions", label: "" },
    ];
};

export const rows = (
    type,
    codes,
    calculatingRValue,
    toggleComponentType,
    toggleCodeType,
    toggleOpenCreate,
    handleEditOpen,
    rValUnits = "RSI"
) => {
    if (type === "Window") {
        return Object.keys(codes)
            .sort((codeA, codeB) => {
                if (codeB > codeA) {
                    return 1;
                }

                if (codeB < codeA) {
                    return -1;
                }

                return 0;
            })
            .map((code) => {
                const [standardCode] = code.split(`${type}-U-`);
                const codeType = standardCode ? "S" : "U";

                const rValue = parseFloat(
                    convertUnit({
                        value: codes[code].nominalRValue,
                        type: "thermalResistance",
                        inputUnit: "RSI",
                        outputUnit: rValUnits,
                    })
                ).toFixed(2);

                return {
                    name: {
                        value: codes[code].label,
                        content: (
                            <span
                                onClick={() =>
                                    handleEditOpen(
                                        code,
                                        {
                                            codeRef: code,
                                            ...codes[code],
                                        },
                                        codes[code].componentType,
                                        codeType
                                    )
                                }
                                className={classes.codeName}
                            >
                                {codes[code].label}
                                {codes[code].warningType && (
                                    <>
                                        <div
                                            className={classes.warning}
                                            data-tip={`<span style="display:block;max-width:16rem;">${getWarningText(
                                                codes[code].warningType
                                            )}</span>`}
                                            data-for={`tip-codelist-${code}`}
                                            data-html
                                        >
                                            <Exclamation />
                                        </div>
                                        <Tooltip id={`tip-codelist-${code}`} />
                                    </>
                                )}
                            </span>
                        ),
                        isLoading: calculatingRValue[code],
                    },
                    type: {
                        value: standardCode ? "Standard" : "User Defined",
                        content: standardCode ? "Standard" : "User Defined",
                        isLoading: calculatingRValue[code],
                    },
                    rVal: {
                        value: rValue,
                        content: `${rValue} ${rValUnits}`,
                        isLoading: calculatingRValue[code],
                    },
                    shgc: {
                        value: codes[code].shgc,
                        content: codes[code].shgc,
                        isLoading: calculatingRValue[code],
                    },
                    lastEdited: {
                        value:
                            codes[code].lastEdited && moment(codes[code].lastEdited).isValid()
                                ? moment(codes[code].lastEdited).format("YYYY-MM-DD")
                                : "0",
                        content:
                            codes[code].lastEdited && moment(codes[code].lastEdited).isValid()
                                ? moment(codes[code].lastEdited).format("MMM DD, YYYY")
                                : "-",
                        isLoading: calculatingRValue[code],
                    },
                    actions: {
                        value: "",
                        content: (
                            <CodeActions
                                type={type}
                                id={code}
                                name={codes[code].label}
                                toggleComponentType={toggleComponentType}
                                toggleCodeType={toggleCodeType}
                                toggleOpenCreate={toggleOpenCreate}
                                handleEditOpen={handleEditOpen}
                            />
                        ),
                    },
                };
            });
    }

    return Object.keys(codes)
        .sort((codeA, codeB) => {
            if (codeB > codeA) {
                return 1;
            }

            if (codeB < codeA) {
                return -1;
            }

            return 0;
        })
        .map((code) => {
            const [standardCode] = code.split(`${type}-U-`);
            const codeType = standardCode ? "S" : "U";

            const nomRValue = parseFloat(
                convertUnit({
                    value: codes[code].nominalRValue || 0,
                    type: "thermalResistance",
                    inputUnit: "RSI",
                    outputUnit: rValUnits,
                })
            ).toFixed(2);

            const effRValue = parseFloat(
                convertUnit({
                    value: codes[code].effectiveRValue || 0,
                    type: "thermalResistance",
                    inputUnit: "RSI",
                    outputUnit: rValUnits,
                })
            ).toFixed(2);

            return {
                name: {
                    value: codes[code].label,
                    content: (
                        <span
                            onClick={() =>
                                handleEditOpen(
                                    code,
                                    {
                                        codeRef: code,
                                        ...codes[code],
                                    },
                                    codes[code].componentType,
                                    codeType
                                )
                            }
                            className={classes.codeName}
                        >
                            {codes[code].label}
                            {codes[code].warningType && (
                                <>
                                    <div
                                        className={classes.warning}
                                        data-tip={`<span style="display:block;max-width:16rem;">${getWarningText(
                                            codes[code].warningType
                                        )}</span>`}
                                        data-for={`tip-codelist-${code}`}
                                        data-html
                                    >
                                        <Exclamation />
                                    </div>
                                    <Tooltip id={`tip-codelist-${code}`} />
                                </>
                            )}
                        </span>
                    ),
                    isLoading: calculatingRValue[code],
                },
                type: {
                    value: standardCode ? "Standard" : "User Defined",
                    content: standardCode ? "Standard" : "User Defined",
                    isLoading: calculatingRValue[code],
                },
                rVal: {
                    value: effRValue,
                    content: `${effRValue} ${rValUnits}`,
                    isLoading: calculatingRValue[code],
                },
                nominalRVal: {
                    value: nomRValue,
                    content: `${nomRValue} ${rValUnits}`,
                    isLoading: calculatingRValue[code],
                },
                lastEdited: {
                    value:
                        codes[code].lastEdited && moment(codes[code].lastEdited).isValid()
                            ? moment(codes[code].lastEdited).format("YYYY-MM-DD")
                            : "0",
                    content:
                        codes[code].lastEdited && moment(codes[code].lastEdited).isValid()
                            ? moment(codes[code].lastEdited).format("MMM DD, YYYY")
                            : "-",
                    isLoading: calculatingRValue[code],
                },
                actions: {
                    value: "",
                    content: (
                        <CodeActions
                            type={type}
                            id={code}
                            name={codes[code].label}
                            toggleComponentType={toggleComponentType}
                            toggleCodeType={toggleCodeType}
                            toggleOpenCreate={toggleOpenCreate}
                            handleEditOpen={handleEditOpen}
                        />
                    ),
                },
            };
        });
};

export const emptyRow = {
    name: {
        value: "",
        isLoading: true,
    },
    rVal: {
        value: "",
        isLoading: true,
    },
    nominalRVal: {
        value: "",
        isLoading: true,
    },
    actions: {
        value: "",
    },
};

const CodeTypeSelect = ({ codeType, toggleCodeType }) => (
    <Select
        className={globalStyles.typeSelect}
        name={"codeType"}
        label="Code Type"
        placeholder="Choose Code Type"
        options={[
            {
                label: "Standard",
                value: "S",
            },
            {
                label: "User Defined",
                value: "U",
            },
        ]}
        disabled={false}
        setValue={codeType}
        input={{
            onChange: (value) => {
                toggleCodeType(value);
            },
            value: codeType,
        }}
    />
);

const CodeList = ({ name, type, rValueUnits, clearInitCode, setInitCode, change, addToCodeLib }) => {
    const [openCreate, toggleOpenCreate] = useState(false);
    const [editOpen, toggleEditOpen] = useState("");
    const [componentType, toggleComponentType] = useState(type);
    const [codeType, toggleCodeType] = useState("S");
    const [calculatingRValues, setCalculatingRValues] = useState(false);

    const codeLib = useStateSelector(`user.codeLib`) || {};
    const { uid } = useStateSelector(`user`) || {};
    const calculatingRValue = useStateSelector(`enclosure.calculatingRValue.codeLib`) || {};

    const { codes = {} } = codeLib[type] || {};

    const totalCount = Object.keys(codes).length;
    const standardCount = Object.keys(codes).filter((code) => code.split(`${type}-U`)[0]).length;
    const uDefCount = Object.keys(codes).filter((code) => !code.split(`${type}-U`)[0]).length;
    const addingNewCode =
        Object.keys(calculatingRValue).some((key) => key.includes(type) && calculatingRValue[key]) &&
        Object.keys(codes).every((key) => !calculatingRValue[key]);

    const handleClose = useCallback(() => {
        toggleOpenCreate(false);
        toggleCodeType("S");
        clearInitCode();
    }, [clearInitCode]);

    const handleEditClose = useCallback(() => {
        toggleEditOpen("");
        toggleCodeType("S");
        clearInitCode();
    }, [clearInitCode]);

    const handleEditOpen = useCallback(
        async (id, code, componentType, codeType) => {
            const codeName = codeType === "S" ? standardCodeFormName(componentType) : uDefCodeFormName(componentType);
            toggleComponentType(componentType);
            toggleCodeType(codeType);
            await setInitCode(code, codeName);
            toggleEditOpen(id);
        },
        [setInitCode]
    );

    //TODO: this in all of the model code drawers - ALSO maybe it's time we make them all one??

    const handleUdefSaveEdit = useCallback(() => {
        toggleCodeType("S");
        toggleEditOpen(false);
    }, []);

    const handleUdefSaveCreate = useCallback(() => {
        toggleCodeType("S");
        toggleOpenCreate(false);
    }, []);

    const handleCalculateRValues = useCallback(async () => {
        if (!uid || !codes) return;

        setCalculatingRValues(true);

        try {
            // Get all codes that don't have effectiveRValue (treating 0 as valid)
            const codesToCalculate = Object.keys(codes).filter((codeRef) => {
                const code = codes[codeRef];
                // Only calculate if effectiveRValue is undefined or null (0 is a valid value)
                return code && (code.effectiveRValue === undefined || code.effectiveRValue === null);
            });

            // Process codes in parallel
            const updatePromises = codesToCalculate.map(async (codeRef) => {
                const code = codes[codeRef];
                const isStandard = codeRef.includes("-S-");
                const isUserDefined = codeRef.includes("-U-");

                try {
                    let effectiveRVal = null;

                    if (isStandard && code.value) {
                        // Use getStandardRVal for standard codes
                        const response = await getStandardRVal({
                            codeString: code.value,
                            codeType: type,
                        });
                        const { data: { rval: { nominalRVal = 0, effectiveRVal: effRVal = nominalRVal } = {} } = {} } =
                            response || {};
                        effectiveRVal = parseFloat(effRVal.toFixed(2));
                    } else if (isUserDefined && code) {
                        // Use getUserDefinedRVal for user-defined codes
                        const response = await getUserDefinedRVal({
                            codeObj: code,
                            codeType: type,
                        });
                        const { data: { rval: { effectiveRVal: effRVal = 0.1 } = {} } = {} } = response || {};
                        effectiveRVal = parseFloat(effRVal.toFixed(2));
                    }

                    if (effectiveRVal !== null) {
                        // Update the code with the calculated effectiveRValue
                        const updatedCode = {
                            ...code,
                            effectiveRValue: effectiveRVal,
                        };
                        await addToCodeLib(uid, updatedCode, type, codeRef);
                    }
                } catch (err) {
                    console.error(`Error calculating R-value for ${codeRef}:`, err);
                }
            });

            await Promise.all(updatePromises);
        } catch (err) {
            console.error("Error calculating R-values:", err);
        } finally {
            setCalculatingRValues(false);
        }
    }, [uid, codes, type, addToCodeLib]);

    return (
        <div className={classes.codeList}>
            <div className={classes.topWrapper}>
                <h2>{name} Codes</h2>
                <div className={classes.stats}>
                    <div className={`${classes.stat} ${classes.largeStat}`}>
                        {/* <img src={ClimateIcon} alt="Sun" /> */}
                        <div>
                            <span className={classes.name}>Total Codes</span>
                            <span className={classes.value}>{totalCount}</span>
                        </div>
                    </div>
                    <div className={classes.stat}>
                        {/* <img src={ClimateIcon} alt="Sun" /> */}
                        <div>
                            <span className={classes.name}>Standard Codes</span>
                            <span className={classes.value}>
                                {standardCount}
                                <small> / {totalCount}</small>
                            </span>
                        </div>
                    </div>
                    <div className={classes.stat}>
                        {/* <img src={ClimateIcon} alt="Sun" /> */}
                        <div>
                            <span className={classes.name}>User Defined Codes</span>
                            <span className={classes.value}>
                                {uDefCount}
                                <small> / {totalCount}</small>
                            </span>
                        </div>
                    </div>
                    <Button
                        className={classes.calculateRValues}
                        onClick={handleCalculateRValues}
                        disabled={calculatingRValues || type === "Lintel" || type === "FloorsAdded"}
                        type="hollow"
                    >
                        {calculatingRValues ? "Calculating..." : "Calculate R-Values"}
                    </Button>
                    <Button
                        className={classes.createCode}
                        icon={() => <AddIcon className={classes.createIcon} />}
                        onClick={() => {
                            toggleOpenCreate(true);
                        }}
                    >
                        Create New Code
                    </Button>
                </div>
            </div>
            <div className={classes.tableWrapper}>
                <Table
                    paginate
                    defaultRowsPerPage={10}
                    headCells={headCells(type)}
                    defaultSortProp="lastEdited"
                    rows={[
                        ...rows(
                            type,
                            codes,
                            calculatingRValue,
                            toggleComponentType,
                            toggleCodeType,
                            toggleOpenCreate,
                            handleEditOpen,
                            rValueUnits
                        ),
                        ...(addingNewCode ? [emptyRow] : []),
                    ]}
                />
                {!Object.keys(codes).length && (
                    <div className={classes.zeroState}>
                        <p>There are no {name} codes to display</p>
                    </div>
                )}
            </div>
            <Drawer
                open={openCreate}
                title={`Create ${name} Code`}
                subtitle={`Define the details of your new ${name.toLowerCase()} code`}
                close={handleClose}
                icon={() => <EditIcon fill="#0eb0a9" />}
                introColor="rgba(24, 193, 173, 0.1)"
                headerClassName={classes.createHeader}
            >
                <CodeDrawerForm
                    toggleOpen={toggleOpenCreate}
                    change={change}
                    fieldAccessor={"none"}
                    componentType={componentType}
                    codeType={codeType}
                    handleClose={handleClose}
                    isCodeLibrary
                    isFlat={type === "CeilingFlat"}
                    handleUdefSave={handleUdefSaveCreate}
                    CodeTypeSelect={!["FloorsAdded", "Lintel"].includes(type) ? () => <CodeTypeSelect codeType={codeType} toggleCodeType={toggleCodeType} /> : null}
                />
            </Drawer>
            <Drawer
                open={!!editOpen}
                title={`Edit ${name} Code`}
                subtitle={
                    <span>
                        Last Modified:{" "}
                        {codes[editOpen] && codes[editOpen].lastEdited
                            ? moment(codes[editOpen].lastEdited).format("MMM DD, YYYY")
                            : "-"}
                        <br />
                        Editing a library code will <strong>not</strong> update instances of the code already used in
                        models.
                    </span>
                }
                close={handleEditClose}
                icon={() => <EditIcon fill="#0b91ea" />}
                headerClassName={classes.editHeader}
                introColor="#f0f8fd"
            >
                <CodeDrawerForm
                    toggleOpen={toggleEditOpen}
                    change={change}
                    fieldAccessor={"none"}
                    componentType={componentType}
                    codeType={codeType}
                    handleClose={handleEditClose}
                    isCodeLibrary
                    isFlat={type === "CeilingFlat"}
                    handleUdefSave={handleUdefSaveEdit}
                />
            </Drawer>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => {
        dispatch(clearInitCode());
    },
    setInitCode: (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    change: (path, value) => dispatch(change(path, value)),
    addToCodeLib: (uid, code, componentType, codeRef) => dispatch(addToCodeLib(uid, code, componentType, codeRef)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CodeList);
